import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { AircallEinstellung, ApiClient } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen } from "@kundeninfo/ui";
import { Eingabe, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../components/meHuellen.js";
import "./AircallEinstellungen.css";

/**
 * DIE SCHWELLE „NACHARBEIT AUFFÄLLIG AB" (Spec „Nacharbeitszeit, Leitung und Kunde je Anruf", Fassung 1.3, E298, F40,
 * F41) im Dienst-Detail Schnittstellen › Aircall. Vorgabe 5 min; ändern darf nur ADMIN, in ganzen Minuten (1–240) im
 * strikten Modal — Speichern ist dort die Bestätigung (Dirk 216c). Die Kennzeichnung wird beim Lesen abgeleitet; der
 * Baustein meldet die geltende Schwelle nach oben (`onSchwelle`), damit Mengen und Anrufzeilen sie anwenden; `null`,
 * wenn sie nicht geladen werden konnte (dann keine Marke nach einer bloß angenommenen Vorgabe). Eine 409 beim Speichern
 * lädt neu, schließt das Modal und nennt den fremden Stand in der Zeile (ME-DESIGN E42, F40). Block `ace`.
 */

export type AircallEinstellungenClient = Pick<ApiClient, "aircallEinstellungen" | "setzeAircallEinstellungen">;

export function AircallEinstellungen(p: {
  client: AircallEinstellungenClient; istAdmin: boolean; onSchwelle: (sekunden: number | null) => void;
}): ReactElement {
  const { client, onSchwelle } = p;
  const [stand, setStand] = useState<AircallEinstellung | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [offen, setOffen] = useState(false);
  const laden = client.aircallEinstellungen;

  useEffect(() => {
    let abgebrochen = false;
    laden().then((s) => { if (!abgebrochen) { setStand(s); onSchwelle(s.acwAuffaelligAbSekunden); } })
      .catch(() => {
        if (!abgebrochen) { setFehler("Die Einstellung konnte nicht geladen werden."); onSchwelle(null); }
      });
    return () => { abgebrochen = true; };
  }, [laden, onSchwelle]);

  function gespeichert(neu: AircallEinstellung): void {
    setStand(neu);
    onSchwelle(neu.acwAuffaelligAbSekunden);
    setOffen(false);
  }

  /** 409: jemand anderes hat die Schwelle geändert — neu laden, Modal schließen, den fremden Stand nennen. */
  function konflikt(): void {
    laden().then((s) => {
      setStand(s);
      onSchwelle(s.acwAuffaelligAbSekunden);
      setOffen(false);
      setHinweis(`Die Schwelle wurde inzwischen auf ${s.acwAuffaelligAbSekunden / 60} min geändert — Stand neu `
        + "geladen.");
    }).catch(() => {
      setOffen(false);
      setFehler("Die Schwelle wurde inzwischen geändert; der neue Stand konnte nicht geladen werden.");
    });
  }

  return (
    <section className="ace" aria-label="Einstellungen">
      <h3 className="ace__titel">Einstellungen</h3>
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {hinweis !== null && <Hinweis art="info">{hinweis}</Hinweis>}
      {stand !== null && (
        <div className="ace__zeile">
          <span>Nacharbeit auffällig ab: {stand.acwAuffaelligAbSekunden / 60} min{stand.vorgabe ? " (Vorgabe)" : ""}</span>
          {p.istAdmin && <KiKnopf onClick={() => { setHinweis(null); setOffen(true); }}>Ändern …</KiKnopf>}
        </div>
      )}
      {stand !== null && (
        <p className="ace__leise">Nur eine Kennzeichnung: Auffällig lange Nacharbeit wird gezählt und gezeigt, nicht
          gekappt — das entscheidet das Regelwerk der Abrechnung.</p>
      )}
      {offen && stand !== null && (
        <SchwelleModal client={client} stand={stand} onFertig={gespeichert} onKonflikt={konflikt}
          onAbbrechen={() => setOffen(false)} />
      )}
    </section>
  );
}

function SchwelleModal(p: { client: AircallEinstellungenClient; stand: AircallEinstellung;
                            onFertig: (neu: AircallEinstellung) => void; onKonflikt: () => void;
                            onAbbrechen: () => void }): ReactElement {
  const ausgang = String(p.stand.acwAuffaelligAbSekunden / 60);
  const [minuten, setMinuten] = useState(ausgang);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function speichern(): Promise<void> {
    const zahl = Number(minuten.trim());
    if (!Number.isInteger(zahl) || zahl < 1 || zahl > 240) {
      setFehler("Bitte eine ganze Zahl von 1 bis 240 Minuten.");
      return;
    }
    setLaeuft(true);
    setFehler(null);
    setMeldung(null);
    try {
      p.onFertig(await p.client.setzeAircallEinstellungen({ version: p.stand.version,
                                                           acwAuffaelligAbSekunden: zahl * 60 }));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        p.onKonflikt();
      } else {
        const amFeld = e instanceof ApiError ? e.fields?.find((f) => f.field === "acwAuffaelligAbSekunden") : undefined;
        if (amFeld !== undefined) setFehler(amFeld.message);
        else setMeldung(e instanceof ApiError ? e.message : "Die Schwelle konnte nicht gespeichert werden.");
      }
    } finally {
      setLaeuft(false);
    }
  }

  async function abbrechen(): Promise<void> {
    if (laeuft) return;
    if (await darfAbbrechen(minuten !== ausgang)) p.onAbbrechen();
  }

  return (
    <Modal titel="Nacharbeit auffällig ab" onAbbrechen={() => { void abbrechen(); }} abbrechenGesperrt={laeuft}>
      {meldung !== null && <Hinweis art="fehler">{meldung}</Hinweis>}
      <Formular beschriftung="oben">
        <Eingabe id="ace-minuten" name="ace-minuten" beschriftung="Auffällig ab" art="zahl" einheit="min"
          wert={minuten} {...(fehler === null ? {} : { fehler })} {...(laeuft ? { gesperrt: true } : {})}
          onGeaendert={(d) => { setMinuten(d.wert); setFehler(null); }} />
      </Formular>
      <ModalFuss speichernText="Speichern" speichernGesperrt={laeuft || minuten === ausgang}
        abbrechenGesperrt={laeuft} onSpeichern={() => { void speichern(); }}
        onAbbrechen={() => { void abbrechen(); }} />
    </Modal>
  );
}
